import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { App, Flex, Input, Spin, Tooltip } from "antd";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { getRouteFindAllByPointId } from "tm-shared/api";
import { PointNode, RouteShort } from "tm-shared/types";
import { validationMessage } from "tm-shared/utils";
import { countText } from "tm-shared/utils";

import { PointCardBase } from "@/domains/point";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { PrimaryText } from "@/shared/ui/Typography";

import { RELATION_TOOLTIPS } from "../DuplicateByAddressModal.constants";
import type { PointRelationsFormValues } from "../DuplicateByAddressModal.types";

interface PointRelationRowProps {
    point: PointNode;
    level?: number;
    formValues: PointRelationsFormValues;
    hasParentSelected: boolean;
    setValue: (name: string, value: string | null) => void;
}

export const PointRelationRow: FC<PointRelationRowProps> = ({
    point,
    level = 0,
    formValues,
    hasParentSelected,
    setValue,
}) => {
    const { notification } = App.useApp();

    const fieldName = point.market.id;
    const currentValue = formValues[fieldName];

    const disableParentOption = hasParentSelected && currentValue !== "parent";
    const [routeCountLoading, setRouteCountLoading] = useState(false);
    const [routes, setRoutes] = useState<RouteShort[]>([]);

    const routesLoaded = useRef<boolean>(false);

    const options = useMemo(
        () => [
            {
                label: "Батьківська ТТ",
                value: "parent",
                disabled: disableParentOption,
            },
            { label: "Незалежна ТТ", value: "independent" },
            { label: "Архівна ТТ", value: "archived" },
        ],
        [disableParentOption],
    );

    const isParent = !!point.child;

    const isArchiveSelectedField = currentValue === "archived";

    const hasRoutes = routes.length > 0;

    useEffect(() => {
        if (
            routesLoaded.current ||
            routeCountLoading ||
            !isArchiveSelectedField
        ) {
            return;
        }

        const fetchRouteCount = async () => {
            try {
                setRouteCountLoading(true);
                const response = await getRouteFindAllByPointId({
                    pointId: point.id,
                });

                setRoutes(response.result);
            } catch (error) {
                notification.error({
                    title: "Не вдалося отримати інформації про маршрути",
                });
            } finally {
                setRouteCountLoading(false);
                routesLoaded.current = true;
            }
        };

        fetchRouteCount();
    }, [notification, isArchiveSelectedField, routeCountLoading, point.id]);

    let status;

    if (point.market.isArchived) {
        status = "archived" as const;
    } else if (hasRoutes && isArchiveSelectedField) {
        status = "hasRoutes" as const;
    }

    const relationKey =
        currentValue ??
        (isParent ? "parent" : point.market.isArchived ? "archived" : "");

    const tooltipText =
        RELATION_TOOLTIPS[relationKey] ?? "Спочатку оберіть відношення";

    return (
        <>
            <PointCardBase
                point={point}
                isChild={level > 0}
                status={status}
                slot={
                    <Flex vertical gap={16}>
                        <Flex align="center" gap={10}>
                            <Tooltip title={tooltipText}>
                                <div>
                                    <InfoCircleOutlined
                                        style={{ fontSize: 18 }}
                                    />
                                </div>
                            </Tooltip>

                            {point.market.isArchived || isParent ? (
                                <>
                                    <Input
                                        disabled
                                        size="large"
                                        value={
                                            isParent
                                                ? "Батьківська ТТ"
                                                : "Архівна ТТ"
                                        }
                                    />
                                </>
                            ) : (
                                <RHFAutocomplete
                                    name={fieldName}
                                    allowClear={false}
                                    placeholder="Оберіть відношення"
                                    options={options}
                                    getOptionDisabled={(option) =>
                                        !!option.disabled
                                    }
                                    rules={{
                                        required: validationMessage.required,
                                    }}
                                    selectProps={{
                                        size: "large",
                                    }}
                                    style={{ width: "100%" }}
                                />
                            )}
                        </Flex>

                        {routeCountLoading ? (
                            <Flex justify={"center"} align={"center"}>
                                <Spin
                                    indicator={<LoadingOutlined spin />}
                                    size="large"
                                />
                            </Flex>
                        ) : (
                            hasRoutes &&
                            isArchiveSelectedField && (
                                <Flex vertical gap={4}>
                                    <PrimaryText textColor="error">
                                        Ця ТТ знаходиться у{" "}
                                        {countText.route(routes.length)}
                                    </PrimaryText>

                                    <Flex>
                                        {routes.map(({ name, uuid }) => (
                                            <PrimaryText
                                                key={uuid}
                                                textColor="disabled"
                                            >
                                                - {name}
                                            </PrimaryText>
                                        ))}
                                    </Flex>
                                </Flex>
                            )
                        )}
                    </Flex>
                }
            />

            {point.child && (
                <PointRelationRow
                    point={point.child}
                    level={level + 1}
                    formValues={formValues}
                    hasParentSelected={hasParentSelected}
                    setValue={setValue}
                />
            )}
        </>
    );
};
